import { useApolloClient, useMutation, useQuery, useReactiveVar } from '@apollo/client';
import _ from 'lodash';

import {
  GET_MEMBERS,
  REMOVE_MEMBER,
  REMOVE_SELF,
  RESTORE_MEMBER,
  UPDATE_MEMBER,
  UPDATE_MEMBERS
} from './member.queries';
import { IMembersResults } from './member.types';
import { IMember, IMemberOverview, Identifiable, ROLE, UserNameable } from '../../../lib/types';
import { useCallback, useMemo } from 'react';
import { IUpdateMemberParams, IUpdateMembersParams } from '../../../lib/query.types';
import { updateMemberCache } from './member.utils';
import { useFetchCurrUser } from '../users/user.service';
import { usePrev } from '../../../lib/utils';
import { USER_COMPANIES_REFETCH_QUERIES } from '../companies/company.const';
import { GET_CURR_USER } from '../users/user.queries';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from '../../../_routes';
import { activeCompanyLoadingVar } from '../companies/company.service';
import { IPermanent } from '../request-group/request-group.types';
import { GET_REQUEST, LIST_REQUESTS_AS_STAFF } from '../request/request.queries';
import { GET_REQUEST_GROUP, GET_REQUEST_GROUPS } from '../request-group/request-group.queries';

export const useCompanyMembers = () => {
  const activeCompanyLoading = useReactiveVar(activeCompanyLoadingVar);
  const { data, ...rest } = useQuery<IMembersResults>(GET_MEMBERS, { fetchPolicy: 'cache-and-network' });

  // Track previous members array for deep equality comparison
  const prevMembers = usePrev(data?.members);

  // Stabilize members array - only change reference if content actually changed
  const stabilizedMembers = useMemo(() => {
    // If no current data, return it as-is
    if (!data?.members) return data?.members;

    // If no previous data, return current (first render)
    if (!prevMembers) return data.members;

    // Deep equality check using lodash - if equal, return previous reference
    const isEqual = _.isEqual(prevMembers, data.members);
    return isEqual ? prevMembers : data.members;
  }, [data?.members, prevMembers]);

  return { ...rest, members: stabilizedMembers, loading: rest.loading || activeCompanyLoading };
};

export const useCurrMember = () => {
  const { members } = useCompanyMembers();
  const { user } = useFetchCurrUser();

  const currMember = useMemo(() => members?.find((member) => member.user._id === user?._id), [members, user?._id]);
  const isAdmin = useMemo(
    () => currMember?.roles.some((r) => [ROLE.COMPANY_ADMIN, ROLE.COMPANY_OWNER].includes(r)),
    [currMember]
  );

  return useMemo(() => ({ currMember, isAdmin }), [currMember, isAdmin]);
};

export const useRemoveMember = () => {
  const client = useApolloClient();

  const [mutation, rest] = useMutation<{ removeMember: IMemberOverview }, Identifiable & IPermanent>(REMOVE_MEMBER, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }, { variables }) => {
      if (data) {
        updateMemberCache({ cache, members: [data.removeMember], remove: variables?.permanent });

        if (variables?.permanent)
          client.refetchQueries({
            include: [GET_MEMBERS, GET_REQUEST, LIST_REQUESTS_AS_STAFF, GET_REQUEST_GROUP, GET_REQUEST_GROUPS]
          });
      }
    }
  });

  const removeMember = useCallback((variables: Identifiable & IPermanent) => mutation({ variables }), [mutation]);

  return { removeMember, ...rest };
};

export const useRemoveSelf = () => {
  const navigate = useNavigate();

  const [mutation, rest] = useMutation<{ removeSelf: IMember }>(REMOVE_SELF, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data) {
        // Remove member from cache and update curr user with new user
        updateMemberCache({ cache, members: [data.removeSelf] });
        cache.writeQuery({ query: GET_CURR_USER, data: { loggedInUser: data.removeSelf.user }, overwrite: true });
        toast.success('You have successfully removed yourself from the organization');

        // If no new active company, then the user has no available companies so let's wipe the curr company cache and navigate to the dashboard
        // TODO: Otherwise the firm profile page won't get the updated null company result (until we revisit the page). Can we force the query to use the updated company result?
        if (!data.removeSelf.user.settings.activeCompanyId) {
          if (cache.evict({ id: 'ROOT_QUERY', fieldName: 'company' })) cache.gc();
          navigate(ROUTE_PATHS.DASHBOARD);
        }
      }
    },
    // Refetch curr user and all curr company members because we will have deleted current member and connection to curr company
    refetchQueries: USER_COMPANIES_REFETCH_QUERIES,
    awaitRefetchQueries: true
  });

  const removeSelf = useCallback(() => {
    activeCompanyLoadingVar(true);
    mutation().finally(() => activeCompanyLoadingVar(false));
  }, [mutation]);

  return { removeSelf, ...rest };
};

export const useRestoreMember = () => {
  const [mutation, rest] = useMutation<{ restoreMember: IMemberOverview }, Identifiable>(RESTORE_MEMBER, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data) updateMemberCache({ cache, members: [data.restoreMember] });
    }
  });

  const restoreMember = useCallback((variables: Identifiable) => mutation({ variables }), [mutation]);

  return { restoreMember, ...rest };
};

export const useUpdateMember = ({ _id }: Identifiable) => {
  const [mutation, rest] = useMutation<{ updateMember: IMemberOverview }, IUpdateMemberParams>(UPDATE_MEMBER, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data) updateMemberCache({ cache, members: [data.updateMember] });
    }
  });

  const updateMember = useCallback(
    (variables: UserNameable) => mutation({ variables: { _id, ...variables } }),
    [_id, mutation]
  );

  return { updateMember, ...rest };
};

export const useUpdateMembers = () => {
  const [mutation, rest] = useMutation<{ updateMembers: IMemberOverview[] }, IUpdateMembersParams>(UPDATE_MEMBERS, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data) updateMemberCache({ cache, members: data.updateMembers });
    }
  });

  const updateMembers = useCallback((variables: IUpdateMembersParams) => mutation({ variables }), [mutation]);

  return { updateMembers, ...rest };
};
